import { Box, Stack, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { throttle } from '../../utils/throttle';
import { drawCroppedImage } from './utils';

interface FinalCropData {
  img: HTMLImageElement;
  canvas: HTMLCanvasElement;
  crop: PixelCrop;
}

const throttledSetFinalCropData = throttle(
  (data: FinalCropData, callback: (data: FinalCropData) => void) => {
    callback(data);
  },
  500
);

const processCenterCrop = (img: HTMLImageElement) => {
  return centerCrop(
    {
      unit: 'px',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
    },
    img.width,
    img.height
  );
};

interface CropperProps {
  imgSrc: string;
  setFinalCroppedData: (data: FinalCropData) => void;
}

const PhotoEditor: React.FC<CropperProps> = ({
  imgSrc,
  setFinalCroppedData,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  // When crop is complete, send data throttled
  const onComplete = useCallback(
    (c: PixelCrop) => {
      setCompletedCrop(c);

      if (c.width && c.height && imgRef.current && previewCanvasRef.current) {
        throttledSetFinalCropData(
          {
            img: imgRef.current,
            crop: c,
            canvas: previewCanvasRef.current,
          },
          setFinalCroppedData
        );
      }
    },
    [setFinalCroppedData]
  );

  // On image load, initialize crop centered and sized by zoom
  const onImageLoad = () => {
    if (imgRef.current) {
      setCrop(processCenterCrop(imgRef.current));
    }
  };

  // Draw cropped image preview as circle
  useEffect(() => {
    drawCroppedImage({ completedCrop, imgRef, previewCanvasRef });
  }, [completedCrop]);

  return (
    <Stack spacing={2} alignItems="center" sx={{ margin: '0 auto' }}>
      {imgSrc && (
        <>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={onComplete}
            circularCrop
            aspect={1}
            minWidth={100}
            minHeight={100}
            ruleOfThirds
            style={{ maxWidth: '100%' }}
          >
            <Box
              component="img"
              ref={imgRef}
              src={imgSrc}
              alt="Profile"
              onLoad={onImageLoad}
              style={{
                maxWidth: '100%',
                objectFit: 'contain',
                userSelect: 'none',
              }}
              draggable={false}
            />
          </ReactCrop>

          <Typography variant="body1">Preview:</Typography>

          <Box
            component="canvas"
            ref={previewCanvasRef}
            style={{
              borderRadius: '50%',
              width: 140,
              height: 140,
              boxShadow: '0 0 5px rgba(0,0,0,0.3)',
            }}
          />
        </>
      )}
    </Stack>
  );
};

export default memo(PhotoEditor);
